import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Eye, EyeOff, Mail, Lock } from 'lucide-react';
import { signInWithGoogle, isOAuthConfigured } from '../../services/oauthService';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [oauthLoading, setOAuthLoading] = useState({ google: false });
  const [showUserTypeModal, setShowUserTypeModal] = useState(false);
  const [selectedUserType, setSelectedUserType] = useState('client');
  const selectedUserTypeRef = useRef('client');

  // Keep ref in sync with state so we always have the latest value at click time
  useEffect(() => {
    selectedUserTypeRef.current = selectedUserType;
  }, [selectedUserType]);

  const { login, oauthLogin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/';

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent double submission
    if (loading) {
      return;
    }
    
    setLoading(true);
    setError('');

    try {
      const result = await login(formData);
      
      if (result.success) {
        navigate(from, { replace: true });
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    if (oauthLoading.google || loading) return;
    
    // Show user type selection modal first
    setShowUserTypeModal(true);
  };

  const handleOAuthWithUserType = async () => {
    if (oauthLoading.google || loading) return;

    // Read from ref to get the value at click time (avoids stale closure if user selects then quickly clicks Continue)
    const userType = selectedUserTypeRef.current;
    
    setOAuthLoading(prev => ({ ...prev, google: true }));
    setError('');
    setShowUserTypeModal(false);

    try {
      const oauthData = await signInWithGoogle();
      const result = await oauthLogin(oauthData, userType);
      
      if (result.success) {
        navigate(from, { replace: true });
      } else {
        if (result.error && typeof result.error === 'object') {
          setError('Please fix the errors below');
        } else {
          setError(result.error || 'Google sign in failed');
        }
      }
    } catch (err) {
      setError(err.message || 'Google sign in failed. Please try again.');
    } finally {
      setOAuthLoading(prev => ({ ...prev, google: false }));
    }
  };

  const oauthConfig = isOAuthConfigured();

  return (
    <div 
      className="min-h-screen flex flex-col justify-end sm:justify-center py-0 sm:py-12 sm:px-6 lg:px-8 relative"
      style={{
        backgroundImage: 'url(/images/01_-_HERO_1.webp)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {/* Dark overlay for better readability */}
      <div className="absolute inset-0 bg-black/50"></div>
      
      <div className="hidden sm:block sm:mx-auto sm:w-full sm:max-w-md relative z-10 mb-8">
        <Link to="/" className="flex justify-center items-center mb-6">
          <img 
            src="/images/VendaLOGO1.png" 
            alt="Venda Logo" 
            className="h-16 w-auto"
          />
        </Link>
        <h2 className="text-center text-3xl font-medium text-white">
          Sign in to your account
        </h2>
        <p className="mt-2 text-center text-sm text-white">
          Or{' '}
          <Link
            to="/register"
            className="font-medium text-white hover:text-gray-200 underline"
          >
            create a new account
          </Link>
        </p>
      </div>

      <div className="w-full sm:mx-auto sm:w-full sm:max-w-md relative z-10 animate-slideUp max-h-[90vh] overflow-y-auto">
        <div className="bg-white py-8 px-6 shadow-2xl rounded-t-3xl sm:rounded-2xl sm:px-10 border-t border-gray-100 sm:border">
          {/* Mobile Header */}
          <div className="sm:hidden mb-6 text-center">
            <Link to="/" className="flex justify-center items-center mb-4">
              <img 
                src="/images/VendaLOGO1.png" 
                alt="Venda Logo" 
                className="h-12 w-auto"
              />
            </Link>
            <h2 className="text-2xl font-medium text-black mb-2">
              Sign in to your account
            </h2>
            <p className="text-sm text-gray-600">
              Or{' '}
              <Link
                to="/register"
                className="font-light text-black hover:text-gray-600 underline"
              >
                create a new account
              </Link>
            </p>
          </div>

          <form className="space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-light text-black mb-1">
                Email address
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  disabled={loading}
                  className="input pl-10 disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="Enter your email"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-light text-black mb-1">
                Password
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  disabled={loading}
                  className="input pl-10 pr-10 disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  disabled={loading}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  disabled={loading}
                  className="h-4 w-4 text-black focus:ring-black border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-black">
                  Remember me
                </label>
              </div>

              <div className="text-sm">
                {loading ? (
                  <span className="font-light text-gray-400 cursor-not-allowed">
                    Forgot your password?
                  </span>
                ) : (
                  <Link
                    to="/forgot-password"
                    className="font-light text-black hover:text-gray-600 underline"
                  >
                    Forgot your password?
                  </Link>
                )}
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center items-center py-3 bg-black text-white font-light rounded-md hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Signing in...
                  </div>
                ) : (
                  'Sign in'
                )}
              </button>
            </div>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-black">Or continue with</span>
              </div>
            </div>

            <div className="mt-6">
              <button
                type="button"
                onClick={handleGoogleSignIn}
                disabled={oauthLoading.google || loading || !oauthConfig.google}
                className="w-full inline-flex justify-center items-center py-2 px-4 border-2 border-black rounded-md shadow-sm bg-white text-sm font-light text-black hover:bg-black hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {oauthLoading.google ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-black"></div>
                ) : (
                  <>
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path
                        fill="currentColor"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="currentColor"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="currentColor"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="currentColor"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                    <span className="ml-2">Google</span>
                  </>
                )}
              </button>

              {/* Facebook button hidden for now */}
              {/* <button
                type="button"
                onClick={handleFacebookSignIn}
                disabled={oauthLoading.facebook || loading || !oauthConfig.facebook}
                className="w-full inline-flex justify-center items-center py-2 px-4 border-2 border-black rounded-md shadow-sm bg-white text-sm font-light text-black hover:bg-black hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {oauthLoading.facebook ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-black"></div>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                    </svg>
                    <span className="ml-2">Facebook</span>
                  </>
                )}
              </button> */}
            </div>
          </div>
        </div>
      </div>

      {/* User Type Selection Modal for OAuth */}
      {showUserTypeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 sm:p-8">
            <h3 className="text-2xl font-medium text-black mb-2">
              Select Account Type
            </h3>
            <p className="text-sm text-gray-600 mb-6">
              Please select whether you're a client looking for vendors or a vendor offering services.
            </p>

            <div className="grid grid-cols-2 gap-3 mb-6">
              <label className="relative">
                <input
                  type="radio"
                  name="user_type"
                  value="client"
                  checked={selectedUserType === 'client'}
                  onChange={(e) => setSelectedUserType(e.target.value)}
                  className="sr-only"
                />
                <div className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                  selectedUserType === 'client'
                    ? 'border-black bg-gray-50 text-black'
                    : 'border-gray-300 hover:border-gray-400'
                }`}>
                  <div className="text-center">
                    <div className="font-medium">Client</div>
                    <div className="text-sm text-gray-500 mt-1">Looking for vendors</div>
                  </div>
                </div>
              </label>
              <label className="relative">
                <input
                  type="radio"
                  name="user_type"
                  value="vendor"
                  checked={selectedUserType === 'vendor'}
                  onChange={(e) => setSelectedUserType(e.target.value)}
                  className="sr-only"
                />
                <div className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                  selectedUserType === 'vendor'
                    ? 'border-black bg-gray-50 text-black'
                    : 'border-gray-300 hover:border-gray-400'
                }`}>
                  <div className="text-center">
                    <div className="font-medium">Vendor</div>
                    <div className="text-sm text-gray-500 mt-1">Offering services</div>
                  </div>
                </div>
              </label>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm mb-4">
                {error}
              </div>
            )}

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowUserTypeModal(false);
                  setError('');
                }}
                disabled={oauthLoading.google || loading}
                className="flex-1 py-2 px-4 border-2 border-gray-300 rounded-md text-sm font-light text-black hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleOAuthWithUserType}
                disabled={oauthLoading.google || loading}
                className="flex-1 py-2 px-4 bg-black text-white rounded-md text-sm font-light hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {oauthLoading.google ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Continuing...
                  </div>
                ) : (
                  'Continue with Google'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
